import SwapHorizIcon from '@offline-mui/icons-material/SwapHoriz'
import { Box, Stack, Typography } from '@offline-mui/material'
import type { ReactNode } from 'react'
import { FONT_MONO } from '../../theme'
import CarrierBadge from './CarrierBadge'

/* ------------------------------------------------------------------ */
/* Time helpers shared by every flight-leg presentation                */
/* ------------------------------------------------------------------ */

/** "HH:MM" from an ISO-like datetime string, without timezone math. */
export function clockFromDatetime(value: string | null | undefined): string {
  if (!value) return '—'
  const match = String(value).match(/[T ](\d{2}:\d{2})/)
  return match ? match[1] : String(value)
}

export function minutesLabel(total: number | null | undefined): string | null {
  if (total == null || !Number.isFinite(total) || total < 0) return null
  const hours = Math.floor(total / 60)
  const mins = Math.round(total % 60)
  if (hours && mins) return `${hours}h ${mins}m`
  if (hours) return `${hours}h`
  return `${mins}m`
}

/** Parses "7:45" / "07:45" / "465" style durations into minutes. */
export function durationToMinutes(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const hm = String(value).match(/^(\d+):(\d{2})$/)
  if (hm) return Number(hm[1]) * 60 + Number(hm[2])
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

/** Wall-clock minutes since epoch for airport-local datetimes (no tz math). */
export function wallClockMinutes(value: string | null | undefined): number | null {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  if (!match) return null
  const ms = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  )
  return Math.round(ms / 60_000)
}

export function calendarDayOffset(departure: string, arrival: string): number {
  const dep = String(departure).slice(0, 10)
  const arr = String(arrival).slice(0, 10)
  if (!dep || !arr || dep === arr) return 0
  const diff = (Date.parse(arr) - Date.parse(dep)) / 86_400_000
  return Number.isFinite(diff) ? Math.round(diff) : 0
}

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */

function TimePoint({
  time,
  airport,
  note,
  align = 'left',
}: {
  time: string
  airport: string
  note?: string | null
  align?: 'left' | 'right'
}) {
  return (
    <Box sx={{ textAlign: align, fontSize: 18, minWidth: '5ch' }}>
      <Typography
        sx={{
          fontSize: 'inherit',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {time}
      </Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', mt: 0.25 }}>
        {airport}
      </Typography>
      {note ? (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'error.main', lineHeight: 1.2 }}>
          {note}
        </Typography>
      ) : null}
    </Box>
  )
}

function DurationBar({ label }: { label: string | null }) {
  return (
    <Box sx={{ flexGrow: 1, minWidth: 72, textAlign: 'center', px: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {label ?? 'Duration —'}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          height: 2,
          bgcolor: 'grey.300',
          borderRadius: 1,
          mt: 0.5,
          mb: 1,
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            top: -3,
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
          },
          '&::before': { left: -1 },
          '&::after': { right: -1 },
        }}
      />
    </Box>
  )
}

/**
 * Grid used by every leg row (and by card headers that must line up with it):
 * identity column · times column · trailing column, collapsing as the card
 * narrows. Wrap the grid in `legContainerSx` so the container queries apply.
 */
export const legContainerSx = { containerType: 'inline-size', containerName: 'legrow' } as const

export function legGridSx(threeColumns: boolean) {
  return {
    display: 'grid',
    alignItems: 'center',
    columnGap: 2,
    rowGap: 1.25,
    gridTemplateColumns: '1fr',
    '@container legrow (min-width: 560px)': {
      gridTemplateColumns: 'minmax(120px, 150px) minmax(0, 1fr)',
      columnGap: 3,
      '& .legrow-trailing': { gridColumn: '2', justifyContent: 'flex-start' },
    },
    '@container legrow (min-width: 760px)': {
      gridTemplateColumns: threeColumns
        ? 'minmax(120px, 150px) minmax(0, 1.25fr) minmax(0, 1fr)'
        : 'minmax(120px, 150px) minmax(0, 1fr)',
      '& .legrow-trailing': { gridColumn: 'auto', justifyContent: 'flex-end' },
    },
  } as const
}

export type FlightLegRowProps = {
  carrier: string
  flightNumber?: string | number | null
  aircraft?: string | null
  origin: string
  destination: string
  departureDatetime?: string | null
  arrivalDatetime?: string | null
  /** Minutes; when omitted it is derived from the two datetimes. */
  durationMinutes?: number | null
  operatedBy?: string | null
  /** Chips or controls rendered at the end of the row. */
  trailing?: ReactNode
  dense?: boolean
}

export function FlightLegRow({
  carrier,
  flightNumber,
  aircraft,
  origin,
  destination,
  departureDatetime,
  arrivalDatetime,
  durationMinutes,
  operatedBy,
  trailing,
  dense = false,
}: FlightLegRowProps) {
  const derived =
    durationMinutes ??
    (() => {
      const dep = wallClockMinutes(departureDatetime)
      const arr = wallClockMinutes(arrivalDatetime)
      return dep != null && arr != null && arr >= dep ? arr - dep : null
    })()
  const offset =
    departureDatetime && arrivalDatetime
      ? calendarDayOffset(departureDatetime, arrivalDatetime)
      : 0

  // The row sizes itself to the card it lives in (not the viewport): one
  // column when narrow, identity + times with the trailing cluster beneath at
  // medium widths, and three columns once there is room for everything.
  return (
    <Box sx={legContainerSx}>
    <Box
      sx={{
        ...legGridSx(Boolean(trailing || aircraft)),
        px: { xs: 2, md: 2.5 },
        py: dense ? 1.5 : 2,
      }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
        <CarrierBadge code={carrier} />
        <Typography sx={{ fontFamily: FONT_MONO, fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}>
          {flightNumber ?? ''}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
        <TimePoint time={clockFromDatetime(departureDatetime)} airport={origin} />
        <DurationBar label={minutesLabel(derived)} />
        <TimePoint
          time={clockFromDatetime(arrivalDatetime)}
          airport={destination}
          note={offset > 0 ? `+${offset} day` : null}
          align="right"
        />
      </Stack>

      {trailing || aircraft ? (
        <Stack
          className="legrow-trailing"
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
        >
          {aircraft ? (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              <Box component="span" sx={{ fontWeight: 500, mr: 0.5 }}>
                Aircraft
              </Box>
              {aircraft}
            </Typography>
          ) : null}
          {trailing}
          {operatedBy ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Operated by {operatedBy}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Box>
    </Box>
  )
}

export function LayoverRow({
  minutes,
  airport,
}: {
  minutes: number | null | undefined
  airport: string
}) {
  const wait = minutesLabel(minutes)
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        px: { xs: 2, md: 2.5 },
        py: 0.75,
        bgcolor: 'grey.50',
        borderTop: '1px dashed',
        borderBottom: '1px dashed',
        borderColor: 'divider',
      }}
    >
      <SwapHorizIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark' }}>
        {wait ? `${wait} layover` : 'Connection'} in {airport}
      </Typography>
    </Stack>
  )
}

/**
 * Header route summary laid out exactly like the times row beneath it (same
 * end-cap widths and gaps) so origin and destination line up with the
 * departure and arrival times. Total time sits above the line; each stop is a
 * red dot on the line.
 */
export function RouteLine({
  origin,
  destination,
  stops,
  totalMinutes,
}: {
  origin: string
  destination: string
  stops: number
  totalMinutes?: number | null
}) {
  const total = minutesLabel(totalMinutes)
  const stopCount = Math.max(0, stops)
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
      <Typography sx={{ minWidth: '5ch', fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
        {origin}
      </Typography>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 72,
          px: 0.5,
          position: 'relative',
          height: 22,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {total ? (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 'calc(50% + 6px)',
              textAlign: 'center',
              color: 'text.secondary',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              lineHeight: 1.3,
            }}
          >
            {total}
          </Typography>
        ) : null}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 2,
            bgcolor: 'grey.300',
            borderRadius: 1,
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: -3,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            },
            '&::before': { left: -1 },
            '&::after': { right: -1 },
          }}
        >
          {Array.from({ length: stopCount }, (_, index) => (
            <Box
              key={index}
              title={`${stopCount} stop${stopCount === 1 ? '' : 's'}`}
              sx={{
                position: 'absolute',
                top: -3,
                left: `${((index + 1) / (stopCount + 1)) * 100}%`,
                transform: 'translateX(-50%)',
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'error.main',
                boxShadow: '0 0 0 2px #fff',
              }}
            />
          ))}
        </Box>
      </Box>
      <Typography
        sx={{ minWidth: '5ch', textAlign: 'right', fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}
      >
        {destination}
      </Typography>
    </Stack>
  )
}

export function StopsChip({ stops }: { stops: number }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        px: 1,
        borderRadius: 2,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: stops === 0 ? '#dcfce7' : 'background.paper',
        color: stops === 0 ? 'success.dark' : 'text.secondary',
        border: stops === 0 ? '1px solid transparent' : '1px solid',
        borderColor: stops === 0 ? 'transparent' : 'grey.300',
      }}
    >
      {stops === 0 ? 'Nonstop' : `${stops} stop${stops === 1 ? '' : 's'}`}
    </Box>
  )
}
